// One circle's members and their latest state, as processed from the realtime
// stream (a port of the Android `LiveCircle`). Pure, so it is unit-tested.
import type { Schemas, WsServerMessage } from "@10twenty/api";

type Member = Schemas["Member"];
type MemberState = Schemas["MemberState"];
type MemberSos = Schemas["MemberSos"];

export interface LiveCircle {
  readonly circleId: string;
  readonly members: Readonly<Record<string, Member>>;
  readonly states: Readonly<Record<string, MemberState>>;
  /** Cursor of the last applied frame; resuming from it skips and repeats nothing. */
  readonly cursor: string | null;
  /**
   * Members a member-list reload reported paused, with the receive sequence of
   * that response. Frames received earlier may predate the pause, so they
   * cannot reveal those members again.
   */
  readonly pausedAsOf: Readonly<Record<string, number>>;
}

/** Frames about one circle (everything but connection housekeeping). */
export type CircleFrame = Extract<
  WsServerMessage,
  {
    type:
      | "subscribed"
      | "unsubscribed"
      | "member_state"
      | "membership"
      | "place_event"
      | "sos"
      | "check_in"
      | "supervision"
      | "supervision_request"
      | "supervision_notice"
      | "error";
  }
>;

/** What the caller must do after applying a frame. */
export type FrameEffect = "none" | "reload_members" | "circle_lost";

export function emptyCircle(circleId: string): LiveCircle {
  return { circleId, members: {}, states: {}, cursor: null, pausedAsOf: {} };
}

/** A fresh snapshot replaces every state; members not in it have none. */
export function withSnapshot(
  circle: LiveCircle,
  cursor: string,
  snapshot: readonly MemberState[],
  receivedAt: number,
): LiveCircle {
  return snapshot.reduce<LiveCircle>((acc, state) => put(acc, state, receivedAt), { ...circle, states: {}, cursor });
}

export function withState(circle: LiveCircle, cursor: string, state: MemberState, receivedAt: number): LiveCircle {
  return put({ ...circle, cursor }, state, receivedAt);
}

/** A `membership` frame's change. */
export interface MembershipChange {
  change: "joined" | "left" | "updated";
  member: Member;
}

export function withMembership(circle: LiveCircle, cursor: string, change: MembershipChange): LiveCircle {
  return { ...changeMembership(circle, change), cursor };
}

function changeMembership(circle: LiveCircle, { change, member }: MembershipChange): LiveCircle {
  const id = member.user_id;
  if (change === "left") {
    return {
      ...circle,
      members: without(circle.members, id),
      states: without(circle.states, id),
      pausedAsOf: without(circle.pausedAsOf, id),
    };
  }
  return { ...circle, members: { ...circle.members, [id]: member }, states: pausedFrom(circle.states, [member]) };
}

/**
 * Replaces the member list from a REST reload received at `asOf`, applying any
 * pause it reports. `since` are the membership frames applied while the
 * request was in flight; the response may predate them. Their member records
 * win over the response, in stream order. Their effect on states was applied
 * with them, in order with state frames, so it is not repeated: a pause or
 * departure replayed here could erase a later resume's location.
 */
export function withMembers(
  circle: LiveCircle,
  list: readonly Member[],
  asOf: number,
  since: readonly MembershipChange[] = [],
): LiveCircle {
  const members: Record<string, Member> = Object.fromEntries(list.map((member) => [member.user_id, member]));
  const newer = new Set<string>();
  for (const { change, member } of since) {
    newer.add(member.user_id);
    if (change === "left") delete members[member.user_id];
    else members[member.user_id] = member;
  }
  const reported = list.filter((member) => !newer.has(member.user_id));
  return {
    ...circle,
    members,
    states: pausedFrom(circle.states, reported),
    pausedAsOf: Object.fromEntries(reported.filter((m) => m.sharing === "paused").map((m) => [m.user_id, asOf])),
  };
}

/**
 * Applies one stream frame received at `receivedAt` (a sequence shared with
 * member-list reloads). Frames for other circles are ignored.
 */
export function applyFrame(
  circle: LiveCircle,
  frame: CircleFrame,
  receivedAt: number,
  selfId: string,
): { circle: LiveCircle; effect: FrameEffect } {
  if (frame.circle_id !== circle.circleId) return { circle, effect: "none" };
  switch (frame.type) {
    case "subscribed":
      if (frame.resumed) return { circle: { ...circle, cursor: frame.cursor }, effect: "none" };
      // Members may have changed while we were away; the snapshot only carries states.
      return { circle: withSnapshot(circle, frame.cursor, frame.snapshot ?? [], receivedAt), effect: "reload_members" };
    case "member_state": {
      const next = withState(circle, frame.cursor, frame.state, receivedAt);
      return { circle: next, effect: hasUnknownMembers(next) ? "reload_members" : "none" };
    }
    case "membership": {
      const next = withMembership(circle, frame.cursor, frame);
      const lost = frame.change === "left" && frame.member.user_id === selfId;
      return { circle: next, effect: lost ? "circle_lost" : "none" };
    }
    case "sos":
      return { circle: withSos(circle, frame.cursor, frame.change, frame.alert), effect: "none" };
    case "place_event":
    case "check_in":
    case "supervision":
    case "supervision_request":
    case "supervision_notice":
      // Place events and check-ins show in history; live alerts are not shown yet, nor is supervision
      // (only its member and guardians get those frames). The frame is still processed.
      return { circle: { ...circle, cursor: frame.cursor }, effect: "none" };
    case "unsubscribed":
      return { circle, effect: "circle_lost" };
    case "error":
      return { circle, effect: frame.code === "forbidden" || frame.code === "not_found" ? "circle_lost" : "none" };
  }
}

/**
 * Applies a `sos` frame to the sender's state: open and acked replace its
 * `MemberState.sos`, resolved clears that alert. A sender without a state yet
 * gets one from the `member_state` frame that follows every `sos` frame.
 */
export function withSos(
  circle: LiveCircle,
  cursor: string,
  change: "open" | "acked" | "resolved",
  alert: Schemas["SosAlert"],
): LiveCircle {
  const id = alert.user_id;
  const state = circle.states[id];
  if (!state) return { ...circle, cursor };
  const sos: MemberSos | null =
    change === "resolved"
      ? state.sos?.id === alert.id
        ? null
        : (state.sos ?? null)
      : alert.resolved_at === null
        ? { id: alert.id, since: alert.started_at, acked_by: alert.acked_by, location: alert.location }
        : null;
  // Without the alert a stored pause applies again at once; the next state frame may never come.
  const paused = sos === null && (id in circle.pausedAsOf || circle.members[id]?.sharing === "paused");
  // An open alert makes the sender visible at once (the attached fix stands in for a hidden one).
  const next = paused ? hidden({ ...state, sos }) : { ...state, sos, ...(sos ? { sharing: "active" as const } : {}) };
  return { ...circle, cursor, states: { ...circle.states, [id]: next } };
}

/** Whether `states` mention someone missing from `members`, so the member list needs a reload. */
export function hasUnknownMembers(circle: LiveCircle): boolean {
  return Object.keys(circle.states).some((id) => !(id in circle.members));
}

function put(circle: LiveCircle, state: MemberState, receivedAt: number): LiveCircle {
  const id = state.user_id;
  const pausedAt = circle.pausedAsOf[id];
  if (pausedAt === undefined) return { ...circle, states: { ...circle.states, [id]: state } };
  // Only a frame received after the reload can be newer than the pause it reported.
  if (receivedAt > pausedAt) {
    return { ...circle, states: { ...circle.states, [id]: state }, pausedAsOf: without(circle.pausedAsOf, id) };
  }
  return { ...circle, states: { ...circle.states, [id]: hidden(state) } };
}

/**
 * A pause learned from a member record hides that member's state at once; the
 * stream's own state frame may never arrive if the connection drops. A resume
 * waits for the next state frame, which carries the location again.
 */
function pausedFrom(
  states: Readonly<Record<string, MemberState>>,
  list: readonly Member[],
): Readonly<Record<string, MemberState>> {
  let next = states;
  for (const member of list) {
    const state = next[member.user_id];
    if (member.sharing === "paused" && state) next = { ...next, [member.user_id]: hidden(state) };
  }
  return next;
}

// An open SOS overrides the stored pause, so the server keeps sending that member.
function hidden(state: MemberState): MemberState {
  if (state.sos) return state;
  return {
    ...state,
    sharing: "paused",
    location: null,
    battery: null,
    place: null,
    motion: null,
    stop: null,
    bubble: null,
  };
}

function without<T>(record: Readonly<Record<string, T>>, key: string): Readonly<Record<string, T>> {
  if (!(key in record)) return record;
  const { [key]: _removed, ...rest } = record;
  return rest;
}

export interface MemberRow {
  userId: string;
  name: string;
  /** The 512 px profile photo (the selected member's row), null without one. */
  avatarUrl: string | null;
  /** The 128 px photo, for markers and list rows. */
  avatarSmallUrl: string | null;
  isSelf: boolean;
  paused: boolean;
  /** An event circle outside its event: nothing about the member is shared (`paused` is true too). */
  outsideEvent: boolean;
  location: Schemas["LiveLocation"] | null;
  battery: Schemas["Battery"] | null;
  lastSeenAt: string | null;
  /** The member's phone has its system Location switched off, so no new fixes can arrive. */
  locationOff: boolean;
  /** Why the member's phones lack full location permission, if they do. */
  locationLimited: Schemas["LocationLimit"] | null;
  /** How the phone was moving at the latest fix. */
  motion: Schemas["MemberMotion"] | null;
  /** The circle's place the member is inside. */
  place: Schemas["CurrentPlace"] | null;
  /** Where the member has stayed, when not inside one of the circle's places. */
  stop: Schemas["MemberStop"] | null;
  /** The member's open SOS alert. */
  sos: MemberSos | null;
  /** The area the member shares instead of their location (approximate sharing); `location` is null then. */
  bubble: Schemas["MemberBubble"] | null;
}

/** Members to show: the caller first, then by name. */
export function memberRows(circle: LiveCircle, selfId: string): MemberRow[] {
  return Object.values(circle.members)
    .sort(
      (a, b) =>
        Number(a.user_id !== selfId) - Number(b.user_id !== selfId) ||
        compare(a.name.toLowerCase(), b.name.toLowerCase()) ||
        compare(a.user_id, b.user_id),
    )
    .map((member) => {
      const state = circle.states[member.user_id];
      // Pauses from member records are folded into states in stream order, so the state is newest.
      const active = (state?.sharing ?? member.sharing) === "active";
      return {
        userId: member.user_id,
        name: member.name,
        avatarUrl: member.avatar_url ?? null,
        avatarSmallUrl: member.avatar_small_url ?? null,
        isSelf: member.user_id === selfId,
        paused: !active,
        outsideEvent: state?.sharing === "outside_event",
        // A paused sender's older fix stays hidden during an SOS; the one sent with the alert shows.
        location: active ? (state?.location ?? state?.sos?.location ?? null) : null,
        battery: active ? (state?.battery ?? null) : null,
        lastSeenAt: state?.last_seen_at ?? null,
        locationOff: active && state?.location_off === true,
        locationLimited: active ? (state?.location_limited ?? null) : null,
        motion: active ? (state?.motion ?? null) : null,
        place: active ? (state?.place ?? null) : null,
        stop: active ? (state?.stop ?? null) : null,
        sos: state?.sos ?? null,
        bubble: active ? (state?.bubble ?? null) : null,
      };
    });
}

function compare(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** A fix older than this is drawn as stale, unless the member is {@link isStationary}. */
export const STALE_AFTER_MS = 30 * 60_000;

/**
 * A stationary phone only sends a heartbeat fix every 10–30 minutes, so it
 * counts as in contact until it has been silent (`last_seen_at`) this long.
 * The server's location-stale alert uses the same hour.
 */
export const QUIET_AFTER_MS = 60 * 60_000;

/**
 * The phone said it was stationary at its latest fix and has been in contact
 * since, so it is still there however old that fix is.
 */
export function isStationary(row: MemberRow, nowMs: number): boolean {
  // With Location off no fix is coming, whatever the phone last reported.
  if (!row.location || row.locationOff || row.motion?.mode !== "stationary" || row.lastSeenAt === null) return false;
  return nowMs - Date.parse(row.lastSeenAt) <= QUIET_AFTER_MS;
}

/** A fix older than this does not count as on the move (the moving trail's window). */
export const MOVING_FIX_MS = 15 * 60_000;

/**
 * The member may be on the move, so the map fetches their recent trail: a fix
 * within {@link MOVING_FIX_MS}, not reported stationary, and at no stop. Inside
 * a place (where the server reports no stop) only a walking or driving fix
 * counts. The trail itself shows only a trip still under way.
 */
export function mightBeMoving(row: MemberRow, nowMs: number): boolean {
  if (!row.location || row.locationOff || row.stop) return false;
  if (nowMs - Date.parse(row.location.ts) > MOVING_FIX_MS) return false;
  const mode = row.motion?.mode;
  if (mode === "stationary") return false;
  return row.place === null || mode !== undefined;
}

/**
 * The location may be out of date: no fix for a while, unless the phone is
 * {@link isStationary}. A phone that went quiet, or reported moving without
 * sending new fixes, is stale. A bubble has no fix time: it is stale once the
 * phone has been silent for {@link QUIET_AFTER_MS}.
 */
export function isStale(row: MemberRow, nowMs: number): boolean {
  if (!row.location) {
    if (!row.bubble || row.lastSeenAt === null) return true;
    return nowMs - Date.parse(row.lastSeenAt) > QUIET_AFTER_MS;
  }
  if (isStationary(row, nowMs)) return false;
  return nowMs - Date.parse(row.location.ts) > STALE_AFTER_MS;
}

/**
 * When the member arrived where they are ("Here since 14:05"): the earliest
 * start of their current place, stop or {@link isStationary} run. Null without
 * one, and while the location may be out of date ({@link isStale}).
 */
export function hereSince(row: MemberRow, nowMs: number): number | null {
  if (!row.location || row.locationOff || isStale(row, nowMs)) return null;
  const starts = [row.place?.since, row.stop?.since, isStationary(row, nowMs) ? row.motion?.since : undefined]
    .filter((since) => since !== undefined)
    .map(Date.parse);
  return starts.length ? Math.min(...starts) : null;
}

/** Where the map draws a member: their fix, or the area they share instead (approximate sharing). */
export interface MapSpot {
  lat: number;
  lon: number;
  /** The accuracy circle's radius, or the bubble's. */
  radiusM: number;
  approximate: boolean;
  /** Drawn faded: out of date ({@link isStale}) or Location off. */
  stale: boolean;
}

export function mapSpot(row: MemberRow, nowMs: number): MapSpot | null {
  if (row.location) {
    const { lat, lon, accuracy_m } = row.location;
    return { lat, lon, radiusM: accuracy_m, approximate: false, stale: row.locationOff || isStale(row, nowMs) };
  }
  if (!row.bubble) return null;
  const { lat, lon, radius_m } = row.bubble;
  return { lat, lon, radiusM: radius_m, approximate: true, stale: row.locationOff || isStale(row, nowMs) };
}

const LOCATION_LIMIT_TEXT: Record<Schemas["LocationLimit"], string> = {
  approximate: "Location limited · approximate only",
  foreground_only: "Location limited · only while the app is open",
  denied: "Location limited · no permission",
};

/** "Location limited · approximate only" and the like, or null with full permission. */
export function locationLimitText(row: MemberRow): string | null {
  return row.locationLimited ? LOCATION_LIMIT_TEXT[row.locationLimited] : null;
}

/** A last battery sample at or below this, discharging, may explain a stale location. */
export const PHONE_OFF_BATTERY_PCT = 5;

/**
 * The battery percentage when the phone may have switched itself off: the
 * location is out of date ({@link isStale}) and the last battery sample was
 * {@link PHONE_OFF_BATTERY_PCT} % or lower and not charging. Else null.
 */
export function phoneMayBeOff(row: MemberRow, nowMs: number): number | null {
  const battery = row.battery;
  if (row.paused || !battery || battery.charging || battery.pct > PHONE_OFF_BATTERY_PCT) return null;
  return isStale(row, nowMs) ? battery.pct : null;
}

/** When the phone last contacted the server, if that is newer than its fix (a minute's grace). */
export function contactAfterFix(row: MemberRow): number | null {
  if (!row.location || row.lastSeenAt === null) return null;
  const seen = Date.parse(row.lastSeenAt);
  return seen - Date.parse(row.location.ts) >= 60_000 ? seen : null;
}

/** "14:05" today, else "Mon 14:05" within a week, else the date. */
export function sinceText(nowMs: number, thenMs: number, locale?: string): string {
  const then = new Date(thenMs);
  const now = new Date(nowMs);
  const time = then.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" });
  if (then.toDateString() === now.toDateString()) return time;
  if (nowMs - thenMs < 6 * 24 * 60 * 60_000) return `${then.toLocaleDateString(locale, { weekday: "short" })} ${time}`;
  return then.toLocaleDateString(locale);
}

/** "just now", "5 min ago", "3 h ago", or null for a day or more (show the date instead). */
export function agoText(nowMs: number, thenMs: number): string | null {
  // Clock skew can put a fresh timestamp slightly in the future.
  const minutes = Math.floor(Math.max(0, nowMs - thenMs) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} h ago`;
  return null;
}

/** Up to two initials for an avatar placeholder. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = (word: string) => (Array.from(word)[0] ?? "").toUpperCase();
  if (words.length === 0) return "?";
  if (words.length === 1) return first(words[0]!);
  return first(words[0]!) + first(words[words.length - 1]!);
}

const AVATAR_COLORS = ["#1e88e5", "#43a047", "#e53935", "#8e24aa", "#fb8c00", "#00897b", "#3949ab", "#6d4c41"];

/** A stable colour per member, the same as on Android (Java `String.hashCode`). */
export function avatarColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (Math.imul(hash, 31) + userId.charCodeAt(i)) | 0;
  const n = AVATAR_COLORS.length;
  return AVATAR_COLORS[((hash % n) + n) % n]!;
}
